import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import api from "../services/api";
import "../styles/Homepage.css";

function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      {
        threshold: options.threshold || 0.08,
        rootMargin: options.rootMargin || "0px 0px -40px 0px",
      },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({
  children,
  dir = "up",
  delay = 0,
  dist = 60,
  className = "",
}) {
  const [ref, visible] = useScrollReveal();
  const transforms = {
    up: `translateY(${dist}px)`,
    down: `translateY(-${dist}px)`,
    left: `translateX(${dist}px)`,
    right: `translateX(-${dist}px)`,
    scale: `scale(0.92)`,
    none: "none",
  };
  return (
    <div
      ref={ref}
      className={`hp-reveal ${className}`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : transforms[dir],
        transition: `opacity 1.1s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 1.1s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

/* ── HERO ── */
const SLIDES = [
  {
    eyebrow: "New Season · 2025",
    title: "Heritage\nLungies",
    sub: "Handwoven in Tamil Nadu — craft in every thread",
    cta: "Shop the Collection",
    category: "Lungies",
    slug: "premium-printed-lungies",
    img: "/assets/lung.png",
    accent: "#f7c863",
    accentDark: "#8a6a35",
  },
  {
    eyebrow: "Handloom Tradition",
    title: "Chennimalai\nBedsheets",
    sub: "Authentic weaves that carry a century of stories",
    cta: "Shop Bedsheets",
    category: "Bedsheets",
    slug: "chennimalai-bedsheets",
    img: "/assets/bedsh.png",
    accent: "#9e8060",
    accentDark: "#735a40",
  },
  {
    eyebrow: "Signature Collection",
    title: "Kaavi\nWeaves",
    sub: "Ochre-dyed, earth-born — rooted in ritual",
    cta: "Discover Kaavi",
    category: "Kaavi",
    slug: "kaavi-collection",
    img: "/assets/kaav.png",
    accent: "#c46a2a",
    accentDark: "#944d1c",
  },
  {
    eyebrow: "Luxury Bath",
    title: "Premium\nTowels",
    sub: "Soft loops. Pure cotton. Enduring quality.",
    cta: "Shop Towels",
    category: "Towel",
    slug: "towel-collection",
    img: "/assets/towe.png",
    accent: "#6b8c7a",
    accentDark: "#4a6558",
  },
];

function HeroSlider() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [prev, setPrev] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const intervalRef = useRef(null);
  const progressRef = useRef(null);

  const goTo = useCallback(
    (idx) => {
      if (transitioning || idx === active) return;
      setTransitioning(true);
      setPrev(active);
      setActive(idx);
      setTimeout(() => {
        setPrev(null);
        setTransitioning(false);
      }, 1200);
    },
    [active, transitioning],
  );

  const next = useCallback(
    () => goTo((active + 1) % SLIDES.length),
    [active, goTo],
  );
  const prev_ = useCallback(
    () => goTo((active - 1 + SLIDES.length) % SLIDES.length),
    [active, goTo],
  );

  useEffect(() => {
    intervalRef.current = setInterval(next, 7000);
    return () => clearInterval(intervalRef.current);
  }, [next]);

  const slide = SLIDES[active];

  return (
    <section className="hp-hero" aria-label="Hero slider">
      {SLIDES.map((s, i) => (
        <div
          key={i}
          className={`hp-hero-slide ${i === active ? "is-active" : ""} ${i === prev ? "is-prev" : ""}`}
          aria-hidden={i !== active}
        >
          <div className="hp-hero-img-wrap">
            <img
              src={s.img}
              alt={s.title.replace("\n", " ")}
              loading={i === 0 ? "eager" : "lazy"}
            />
          </div>
          <div className="hp-hero-veil" />
        </div>
      ))}

      <div className="hp-hero-content" key={active}>
        <div className="hp-hero-inner">
          <div className="hp-hero-eyebrow-wrap">
            <span className="hp-hero-eyebrow-line" />
            <span className="hp-hero-eyebrow">{slide.eyebrow}</span>
          </div>
          <h1 className="hp-hero-title" style={{ whiteSpace: "pre-line" }}>
            {slide.title}
          </h1>
          <p className="hp-hero-sub">{slide.sub}</p>
          <div className="hp-hero-actions">
            <button
              className="hp-hero-btn hp-hero-btn--primary"
              style={{ "--accent": slide.accent }}
              onClick={() =>
                navigate("/collection", {
                  state: { category: slide.category, slug: slide.slug },
                })
              }
            >
              <span>{slide.cta}</span>
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
            <Link to="/collection" className="hp-hero-btn hp-hero-btn--ghost">
              All Collections
            </Link>
          </div>
        </div>
      </div>

      {/* Slide counter + vertical progress */}
      <div className="hp-hero-counter">
        <span className="hp-hero-counter-num">
          {String(active + 1).padStart(2, "0")}
        </span>
        <div className="hp-hero-counter-track">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`hp-hero-counter-seg ${i === active ? "is-active" : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
            >
              <span className="hp-hero-counter-fill" />
            </button>
          ))}
        </div>
        <span className="hp-hero-counter-total">
          {String(SLIDES.length).padStart(2, "0")}
        </span>
      </div>

      {/* Arrow pair */}
      <div className="hp-hero-arrows">
        <button className="hp-hero-arrow" onClick={prev_} aria-label="Previous">
          <svg
            viewBox="0 0 24 24"
            width="15"
            height="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button className="hp-hero-arrow" onClick={next} aria-label="Next">
          <svg
            viewBox="0 0 24 24"
            width="15"
            height="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* Scrolling text strip at bottom */}
      {/* <div className="hp-hero-marquee" aria-hidden="true">
        <div className="hp-hero-marquee-track">
          {[
            "Heritage · Cotton · Handloom · Tamil Nadu · Kaavi · Crafted · Since 1982 · ",
          ]
            .concat([
              "Heritage · Cotton · Handloom · Tamil Nadu · Kaavi · Crafted · Since 1982 · ",
            ])
            .map((t, i) => (
              <span key={i}>{t}</span>
            ))}
        </div>
      </div>*/}
    </section>
  );
}

/* ── TICKER ── */
function Ticker() {
  const items = [
    "Free Shipping above ₹1,000",
    "100% Pure Cotton",
    "7-Day Easy Returns",
    "Handwoven in Tamil Nadu",
    "Kaavi Dyed Heritage Textiles",
    "New Season Now In",
  ];
  const full = [...items, ...items, ...items];
  return (
    <div className="hp-ticker" aria-hidden="true">
      <div className="hp-ticker-track">
        {full.map((item, i) => (
          <span key={i} className="hp-ticker-item">
            {item}
            <span className="hp-ticker-sep">◆</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── TRUST BAR ── */
const TRUST = [
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        width="20"
        height="20"
      >
        <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" />
        <path
          d="M3 17V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10"
          strokeLinecap="round"
        />
      </svg>
    ),
    label: "Free Shipping",
    sub: "Orders above ₹1,000",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        width="20"
        height="20"
      >
        <polyline points="1 4 1 10 7 10" />
        <path d="M3.51 15a9 9 0 1 0 .49-4.5" strokeLinecap="round" />
      </svg>
    ),
    label: "Easy Returns",
    sub: "7-day return policy",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        width="20"
        height="20"
      >
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" />
      </svg>
    ),
    label: "Secure Payment",
    sub: "100% safe checkout",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        width="20"
        height="20"
      >
        <path
          d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
          strokeLinecap="round"
        />
      </svg>
    ),
    label: "Authentic Weave",
    sub: "Heritage certified",
  },
];

function TrustBar() {
  return (
    <div className="hp-trust">
      <div className="hp-container">
        {TRUST.map((t, i) => (
          <Reveal key={t.label} dir="up" delay={i * 60}>
            <div className="hp-trust-item">
              <span className="hp-trust-icon">{t.icon}</span>
              <div className="hp-trust-text">
                <strong>{t.label}</strong>
                <span>{t.sub}</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

/* ── SECTION HEADER ── */
function SectionHeader({ eyebrow, title, sub, to, state, align = "left" }) {
  return (
    <div className={`hp-sec-header hp-sec-header--${align}`}>
      <Reveal dir="up">
        {eyebrow && (
          <span className="hp-sec-eyebrow">
            <span className="hp-sec-eyebrow-line" />
            {eyebrow}
          </span>
        )}
        <h2 className="hp-sec-title">{title}</h2>
        {sub && <p className="hp-sec-sub">{sub}</p>}
      </Reveal>
      {to && (
        <Reveal dir="up" delay={120}>
          <Link to={to} state={state} className="hp-sec-link">
            View all
            <svg
              viewBox="0 0 24 24"
              width="12"
              height="12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </Link>
        </Reveal>
      )}
    </div>
  );
}

/* ── FEATURED CATEGORIES ── */
const CATEGORIES = [
  {
    label: "Lungies",
    eyebrow: "Best Seller",
    img: "/assets/lung.png",
    category: "Lungies",
    slug: "premium-printed-lungies",
    size: "tall",
  },
  {
    label: "Bedsheets",
    eyebrow: "Home Comfort",
    img: "/assets/bedsh.png",
    category: "Bedsheets",
    slug: "bedsheets",
    size: "normal",
  },
  {
    label: "Kaavi",
    eyebrow: "Traditional",
    img: "/assets/kaav.png",
    category: "Kaavi",
    slug: "kaavi-collection",
    size: "normal",
  },
  {
    label: "Towels",
    eyebrow: "Luxury Bath",
    img: "/assets/towe.png",
    category: "Towel",
    slug: "towel-collection",
    size: "normal",
  },
];

function FeaturedCategories() {
  const navigate = useNavigate();
  return (
    <section className="hp-section hp-categories-section">
      <div className="hp-container">
        <SectionHeader
          eyebrow="Shop by Category"
          title="Explore the Collection"
          to="/collection"
        />
        <div className="hp-cat-grid">
          {CATEGORIES.map((c, i) => (
            <Reveal
              key={c.label}
              dir="up"
              delay={i * 80}
              className={`hp-cat-cell ${c.size === "tall" ? "hp-cat-cell--tall" : ""}`}
            >
              <button
                className="hp-cat-card"
                onClick={() =>
                  navigate("/collection", {
                    state: { category: c.category, slug: c.slug },
                  })
                }
                aria-label={`Shop ${c.label}`}
              >
                <div className="hp-cat-img">
                  <img src={c.img} alt={c.label} loading="lazy" />
                </div>
                <div className="hp-cat-veil" />
                <div className="hp-cat-body">
                  <span className="hp-cat-eyebrow">{c.eyebrow}</span>
                  <span className="hp-cat-label">{c.label}</span>
                  <span className="hp-cat-cta">
                    Shop Now
                    <svg
                      viewBox="0 0 24 24"
                      width="10"
                      height="10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </span>
                </div>
                <div className="hp-cat-number">0{i + 1}</div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── PRODUCT ROW ── */
function ProductRow({ eyebrow, title, sub, category, slug, limit = 4 }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.products
      .getAll({ category, subcategory: slug, limit, sort: "featured" })
      .then((d) => setProducts(d.products || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, slug, limit]);
  if (!loading && products.length === 0) return null;
  return (
    <section className="hp-section">
      <div className="hp-container">
        <SectionHeader
          eyebrow={eyebrow}
          title={title}
          sub={sub}
          to="/collection"
          state={{ category, slug }}
        />
        <div className="hp-prod-grid">
          {loading
            ? [...Array(limit)].map((_, i) => (
                <div key={i} className="hp-prod-skeleton" style={{ "--i": i }}>
                  <div className="hp-prod-skeleton-img" />
                  <div
                    className="hp-prod-skeleton-line"
                    style={{ width: "72%" }}
                  />
                  <div
                    className="hp-prod-skeleton-line"
                    style={{ width: "44%" }}
                  />
                </div>
              ))
            : products.map((p, i) => (
                <Reveal key={p._id} dir="up" delay={i * 90}>
                  <ProductCard product={p} />
                </Reveal>
              ))}
        </div>
      </div>
    </section>
  );
}

/* ── EDITORIAL BANNER ── */
function EditorialBanner({
  eyebrow,
  title,
  sub,
  cta,
  img,
  category,
  slug,
  flip = false,
}) {
  const navigate = useNavigate();
  const [ref, visible] = useScrollReveal({ threshold: 0.08 });
  return (
    <div
      ref={ref}
      className={`hp-editorial ${flip ? "hp-editorial--flip" : ""} ${visible ? "is-visible" : ""}`}
      onClick={() => navigate("/collection", { state: { category, slug } })}
      role="button"
      tabIndex={0}
      onKeyDown={(e) =>
        e.key === "Enter" &&
        navigate("/collection", { state: { category, slug } })
      }
      aria-label={`Shop ${title}`}
    >
      <div className="hp-editorial-img">
        <img src={img} alt={title} loading="lazy" />
        <div className="hp-editorial-img-veil" />
      </div>
      <div className="hp-editorial-body">
        <div className="hp-editorial-body-inner">
          <span className="hp-editorial-tag">{eyebrow}</span>
          <h2 className="hp-editorial-title">{title}</h2>
          {sub && <p className="hp-editorial-sub">{sub}</p>}
          <div className="hp-editorial-cta-wrap">
            <span className="hp-editorial-cta">
              {cta}
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── PROMO BANNER ── */
function PromoBanner({
  tag,
  headline,
  sub,
  cta,
  img,
  badgeText,
  category,
  slug,
}) {
  const navigate = useNavigate();
  const [ref, visible] = useScrollReveal({ threshold: 0.06 });
  return (
    <div ref={ref} className={`hp-promo ${visible ? "is-visible" : ""}`}>
      <img src={img} alt={headline} className="hp-promo-bg" loading="lazy" />
      <div className="hp-promo-veil" />
      {badgeText && <div className="hp-promo-badge">{badgeText}</div>}
      <div className="hp-promo-content">
        {tag && (
          <span className="hp-promo-tag">
            <span className="hp-promo-tag-line" />
            {tag}
          </span>
        )}
        <h2 className="hp-promo-headline">{headline}</h2>
        {sub && <p className="hp-promo-sub">{sub}</p>}
        <button
          className="hp-promo-btn"
          onClick={() => navigate("/collection", { state: { category, slug } })}
        >
          <span>{cta}</span>
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ── WHY KKB ── */
const WHY = [
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        width="26"
        height="26"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    title: "Traditional Weave",
    desc: "Each piece handcrafted using techniques passed through generations of Tamil Nadu artisans.",
    note: "100% Handloom",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        width="26"
        height="26"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
    title: "Premium Cotton",
    desc: "Sourced from the finest farms, woven into fabric with exceptional durability and breathability.",
    note: "Premium Grade",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        width="26"
        height="26"
      >
        <circle cx="12" cy="12" r="10" />
        <path
          d="M8.56 2.75c4.37 6.03 6.02 9.42 8.03 17.72m2.54-15.38c-3.72 4.35-8.94 5.66-16.88 5.85m19.5 1.9c-3.5-.93-6.63-.82-8.94 0-2.58.92-5.01 2.86-7.44 6.32"
          strokeLinecap="round"
        />
      </svg>
    ),
    title: "Natural Dyes",
    desc: "Our Kaavi collection uses ancient natural dyeing — earth pigments that age beautifully over time.",
    note: "Eco-Conscious",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        width="26"
        height="26"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </svg>
    ),
    title: "Lifetime Quality",
    desc: "Textiles built to last — proper care makes these pieces heirlooms, not fast fashion.",
    note: "Enduring Craft",
  },
];

function WhyKKB() {
  return (
    <section className="hp-section hp-why-section">
      <div className="hp-container">
        <SectionHeader
          eyebrow="Our Promise"
          title="Why KKB & Co."
          align="center"
        />
        <div className="hp-why-grid">
          {WHY.map((w, i) => (
            <Reveal key={w.title} dir="up" delay={i * 90}>
              <div className="hp-why-card">
                <div className="hp-why-icon-wrap">
                  <div className="hp-why-icon">{w.icon}</div>
                </div>
                <h3 className="hp-why-title">{w.title}</h3>
                <p className="hp-why-desc">{w.desc}</p>
                <span className="hp-why-note">{w.note}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── TESTIMONIALS ── */
const REVIEWS = [
  {
    name: "Rajesh M.",
    location: "Chennai",
    date: "2 weeks ago",
    rating: 5,
    text: "The quality is unlike anything I've found online. The weave is tight, the colours don't bleed. My father wore a KKB lungi for 15 years — now I understand why.",
    tag: "Verified Buyer",
  },
  {
    name: "Priya S.",
    location: "Bangalore",
    date: "1 month ago",
    rating: 5,
    text: "Ordered the Chennimalai bedsheets and they're absolutely gorgeous. The fabric gets softer after every wash. Will be gifting these this Diwali.",
    tag: "Repeat Customer",
  },
  {
    name: "Kumar T.",
    location: "Coimbatore",
    date: "3 days ago",
    rating: 5,
    text: "The Kaavi collection is stunning. You can feel the tradition in the texture. Fast delivery, beautiful packaging, and the quality exceeded expectations.",
    tag: "Verified Buyer",
  },
  {
    name: "Meena R.",
    location: "Madurai",
    date: "Recent",
    rating: 4,
    text: "Very happy with the towels — thick, absorbent, zero lint. Customer service was helpful when I had a sizing query. Recommended to all my friends.",
    tag: "Verified Buyer",
  },
];

const StarIcon = ({ fill }) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill={fill ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="2"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export function Testimonials() {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo =
        direction === "left"
          ? scrollLeft - clientWidth
          : scrollLeft + clientWidth;
      scrollRef.current.scrollTo({ left: scrollTo, behavior: "smooth" });
    }
  };

  return (
    <section className="hp-testimonials">
      <div className="hp-containertest">
        <header className="hp-testimonials-header">
          <div className="hp-header-left">
            <span className="hp-eyebrow">Real Stories</span>
            <h2 className="hp-title">What our community says</h2>
          </div>
          <div className="hp-trust-badge">
            <div className="hp-stars-group">
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} fill={true} />
              ))}
            </div>
            <p>
              <strong>4.9/5</strong> based on 1,200+ reviews
            </p>
          </div>
        </header>

        <div className="hp-slider-controls">
          <button onClick={() => scroll("left")} aria-label="Previous">
            ←
          </button>
          <button onClick={() => scroll("right")} aria-label="Next">
            →
          </button>
        </div>
      </div>

      <div className="hp-reviews-viewport" ref={scrollRef}>
        <div className="hp-reviews-track">
          {REVIEWS.map((r, i) => (
            <article key={i} className="hp-review-card">
              <div className="hp-card-inner">
                <div className="hp-review-top">
                  <div className="hp-stars">
                    {[...Array(5)].map((_, s) => (
                      <StarIcon key={s} fill={s < r.rating} />
                    ))}
                  </div>
                  <span className="hp-review-date">{r.date}</span>
                </div>

                <blockquote className="hp-review-text">
                  &ldquo;{r.text}&rdquo;
                </blockquote>

                <footer className="hp-review-footer">
                  <div className="hp-avatar">{r.name.charAt(0)}</div>
                  <div className="hp-meta">
                    <div className="hp-name-row">
                      <span className="hp-name">{r.name}</span>
                      {r.tag && <span className="hp-badge">✓ {r.tag}</span>}
                    </div>
                    <p className="hp-location">{r.location}</p>
                  </div>
                </footer>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── CRAFT STORY SECTION ── */
function CraftStory() {
  const [ref, visible] = useScrollReveal({ threshold: 0.1 });
  return (
    <section ref={ref} className={`hp-craft ${visible ? "is-visible" : ""}`}>
      <div className="hp-craft-grid">
        <div className="hp-craft-media">
          <div className="hp-craft-img-main">
            <img
              src="/assets/kaav.png"
              alt="Kaavi weave process"
              loading="lazy"
            />
          </div>
          <div className="hp-craft-img-accent">
            <img src="/assets/lung.png" alt="Lungi weaving" loading="lazy" />
          </div>
          <div className="hp-craft-stat">
            <span className="hp-craft-stat-num">40+</span>
            <span className="hp-craft-stat-label">Years of craft</span>
          </div>
        </div>
        <div className="hp-craft-body">
          <Reveal dir="right">
            <span className="hp-sec-eyebrow">
              <span className="hp-sec-eyebrow-line" />
              The Craft Story
            </span>
            <h2 className="hp-craft-title">
              Where every thread
              <br />
              <em>tells a story</em>
            </h2>
            <p className="hp-craft-text">
              Nestled in the weaving heartland of Chennimalai, Tamil Nadu, KKB &
              Co. has been preserving the ancient art of handloom for over four
              decades. Each textile begins with the finest cotton, hand-spun and
              dyed using earth pigments, then woven by master artisans on
              traditional pit looms.
            </p>
            <p className="hp-craft-text" style={{ marginTop: "16px" }}>
              The result is fabric that carries memory — soft, durable, and
              alive with the spirit of the craftspeople who made it.
            </p>
            <Link to="/about" className="hp-craft-link">
              Our Story
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ── NEWSLETTER ── */
function Newsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) return;
    setStatus("sending");
    await new Promise((r) => setTimeout(r, 1200));
    setStatus("done");
  };
  return (
    <section className="hp-newsletter">
      <div className="hp-newsletter-inner">
        <Reveal dir="up">
          <span className="hp-newsletter-eyebrow">Join the Community</span>
          <h2 className="hp-newsletter-title">
            Craft stories, seasonal drops,
            <br />
            and exclusive offers.
          </h2>
          <p className="hp-newsletter-sub">
            Be the first to know. No spam, ever.
          </p>
        </Reveal>
        <Reveal dir="up" delay={120}>
          {status === "done" ? (
            <p className="hp-newsletter-success">
              ✦ &nbsp;Welcome to the KKB family.
            </p>
          ) : (
            <form className="hp-newsletter-form" onSubmit={handleSubmit}>
              <input
                className="hp-newsletter-input"
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button
                className="hp-newsletter-btn"
                type="submit"
                disabled={status === "sending"}
              >
                {status === "sending" ? (
                  <span className="hp-newsletter-spinner" />
                ) : (
                  "Subscribe"
                )}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

/* ── FLOATING CART ── */
function FloatingCart() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 400);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <Link
      to="/cart"
      className={`hp-float-cart ${scrolled ? "hp-float-cart--visible" : ""}`}
      aria-label="View cart"
    >
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
      <span>Cart</span>
    </Link>
  );
}

/* ── HOME PAGE ── */
export default function HomePage() {
  return (
    <div className="hp-page">
      <div className="hp-grain" aria-hidden="true" />
      <HeroSlider />
      {/* <Ticker /> */}
      <TrustBar />
      <FeaturedCategories />
      <ProductRow
        eyebrow="Just Arrived"
        title="New Arrivals"
        sub="Fresh weaves for the new season"
        category="Lungies"
        slug="premium-printed-lungies"
        limit={4}
      />
      <EditorialBanner
        eyebrow="The Handloom Story"
        title="Chennimalai Bedsheets"
        sub="Authentic handloom tradition from the heart of Tamil Nadu — every thread a story."
        cta="Shop the Collection"
        img="/assets/bedsh.png"
        category="Bedsheets"
        slug="chennimalai-bedsheets"
      />
      <ProductRow
        eyebrow="Heritage Weave"
        title="Checked Lungies"
        sub="Classic checks, endlessly wearable"
        category="Lungies"
        slug="checked-lungies"
        limit={4}
      />
      <CraftStory />
      <PromoBanner
        tag="Limited Edition"
        headline="Up to 40% Off — Season Sale"
        sub="Our biggest sale of the year. Pure cotton, deep discounts, heritage quality."
        cta="Shop the Sale"
        img="/assets/kaav.png"
        badgeText="SALE"
        category="Lungies"
        slug="premium-printed-lungies"
      />
      <ProductRow
        eyebrow="Home Comfort"
        title="Bedsheets Collection"
        category="Bedsheets"
        slug="bedsheets"
        limit={4}
      />
      <EditorialBanner
        eyebrow="Earth-Dyed · Hand-Loomed"
        title="Kaavi Collection"
        sub="Ochre pigments, ritual patterns — rooted in south Indian tradition."
        cta="Explore Kaavi"
        img="/assets/kaav.png"
        category="Kaavi"
        slug="kaavi-collection"
        flip
      />
      <ProductRow
        eyebrow="Luxury Bath"
        title="Towel Collection"
        sub="Soft. Absorbent. Enduring."
        category="Towel"
        slug="towel-collection"
        limit={4}
      />
      <WhyKKB />
      <Testimonials />
      <Newsletter />
      <FloatingCart />
    </div>
  );
}
